/* ══════════════════════════════════════════════════════════════════════════
 * Discount badge on product cards.
 *
 * The API marks a variation whose DISCOUNT PRICE is the charged tier with
 * `discounted: true` and carries the pre-discount list price as
 * `originalPrice` / `originalPriceRaw` (a shop or class price override wins
 * over the discount, and then `discounted` is false). Every card mapper
 * spreads `discountCardFields()` into its card so the DS ProductCard shows the
 * original price crossed out, the new price in red and a localized badge
 * ("-20% ALENNUS" on FI, "-20% TILBUD" on DK, "-20% REA" on SE / ABC).
 * ══════════════════════════════════════════════════════════════════════════ */

/** Badge word per brand, keyed by route slug AND api slug. */
export const DISCOUNT_BADGE_COPY = {
  'cls-dk': 'TILBUD', clsdk: 'TILBUD',
  'cls-se': 'REA',    clsse: 'REA',
  'cls-fi': 'ALENNUS', clsfi: 'ALENNUS',
  'abc-se': 'REA',    abcse: 'REA',
};

export const discountBadgeWord = (brand) => DISCOUNT_BADGE_COPY[brand] || 'SALE';

/** Whole-percent saving, or 0 when the numbers do not describe a saving. */
export function discountPercent(priceRaw, originalPriceRaw) {
  const p = Number(priceRaw);
  const o = Number(originalPriceRaw);
  if (!Number.isFinite(p) || !Number.isFinite(o) || o <= 0 || p >= o) return 0;
  return Math.round((1 - p / o) * 100);
}

/** Localized badge text: "-20% ALENNUS"; without a percentage just the word. */
export function discountBadgeText(entry, brand) {
  const pct = discountPercent(entry?.priceRaw, entry?.originalPriceRaw);
  const word = discountBadgeWord(brand);
  return pct > 0 ? `-${pct}% ${word}` : word;
}

export const NO_DISCOUNT_FIELDS = Object.freeze({ priceState: 'default', originalPrice: '', discountBadgeText: '' });

/**
 * Card fields for an API entry (variation / listing card / card-prices hit):
 * the discounted state with the crossed-out original and the badge when the
 * discount is the charged tier, else the explicit defaults (so an overlay can
 * RESET a listing's discount when the visitor's shop overrides the price).
 */
export function discountCardFields(entry, brand) {
  if (!entry?.discounted || !entry.originalPrice) return { ...NO_DISCOUNT_FIELDS };
  return {
    priceState: 'discounted',
    originalPrice: entry.originalPrice,
    discountBadgeText: discountBadgeText(entry, brand),
  };
}
